import './box-score.styles.scss';
import { Link } from 'react-router-dom';
import { positions } from '@/utils/positions';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import SortTh from '@/components/SHARED/sort-th/sort-th.component';
import { useSortable } from '@/utils/useSortable';

// One team's box score from a simulated game.
// teamResult: result.home / result.away from the engine.
// players: the team's roster {id: player}, for names and positions.
// playerLink(teamId, playerId): optional path for each player's page.
const BoxScore = ({ teamResult, players, playerLink }) => {

    const sorter = useSortable();
    const rows = sorter.apply(Object.entries(teamResult.players)
        .map(([id, s]) => ({ ...s, id, player: players[id] }))
        .sort((a, b) => (b.starter - a.starter) || (b.min - a.min)), {
        name: r => r.player?.lName ?? '', pos: r => r.player?.pos1, min: r => r.min || null,
        pts: r => r.pts, fg: r => r.fgm, tp: r => r.tpm, ft: r => r.ftm, reb: r => r.oreb + r.dreb,
        ast: r => r.ast, stl: r => r.stl, blk: r => r.blk, tov: r => r.tov, pf: r => r.pf, pm: r => r.pm,
    });
    const th = (k, label, first) => <SortTh k={k} label={label} sorter={sorter} first={first} className={k === 'name' || k === 'pos' ? '' : 'num'} />;
    const t = teamResult.totals;

    const shooting = (made, att) => `${made}-${att}`;
    const pct = (made, att) => att ? `${Math.round(made / att * 100)}%` : '-';

    return (
        <div className='box-score'>
            <div className='box-score-title'>
                <span><TeamName team={teamResult} /></span>
                <span className='box-score-total'>{teamResult.score}</span>
            </div>
            <div className='box-score-scroll'>
                <table className='g-table box-score-table'>
                    <thead>
                        <tr>
                            {th('name', 'Player', 'asc')}
                            {th('pos', 'Pos', 'asc')}
                            {th('min', 'Min')}
                            {th('pts', 'Pts')}
                            {th('fg', 'FG')}
                            {th('tp', '3PT')}
                            {th('ft', 'FT')}
                            {th('reb', 'Reb')}
                            {th('ast', 'Ast')}
                            {th('stl', 'Stl')}
                            {th('blk', 'Blk')}
                            {th('tov', 'TO', 'asc')}
                            {th('pf', 'PF', 'asc')}
                            {th('pm', '+/-')}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id} className={r.starter ? 'starter' : ''}>
                                <td>
                                    {r.player && playerLink ?
                                        <Link to={playerLink(teamResult.id, r.id)}>{r.player.fName} {r.player.lName}</Link> :
                                        r.player ? `${r.player.fName} ${r.player.lName}` : r.id}
                                </td>
                                <td>{positions[(r.player?.pos1 ?? 1) - 1].short}</td>
                                <td className='num'>{r.min ? Math.round(r.min) : '-'}</td>
                                <td className='num'>{r.pts}</td>
                                <td className='num'>{shooting(r.fgm, r.fga)}</td>
                                <td className='num'>{shooting(r.tpm, r.tpa)}</td>
                                <td className='num'>{shooting(r.ftm, r.fta)}</td>
                                <td className='num'>{r.oreb + r.dreb}</td>
                                <td className='num'>{r.ast}</td>
                                <td className='num'>{r.stl}</td>
                                <td className='num'>{r.blk}</td>
                                <td className='num'>{r.tov}</td>
                                <td className='num'>{r.pf}</td>
                                <td className='num'>{r.pm > 0 ? `+${r.pm}` : r.pm}</td>
                            </tr>
                        ))}
                        <tr className='totals'>
                            <td>Totals</td>
                            <td></td>
                            <td></td>
                            <td className='num'>{t.pts}</td>
                            <td className='num'>{shooting(t.fgm, t.fga)}<div className='meta'>{pct(t.fgm, t.fga)}</div></td>
                            <td className='num'>{shooting(t.tpm, t.tpa)}<div className='meta'>{pct(t.tpm, t.tpa)}</div></td>
                            <td className='num'>{shooting(t.ftm, t.fta)}<div className='meta'>{pct(t.ftm, t.fta)}</div></td>
                            <td className='num'>{t.oreb + t.dreb}</td>
                            <td className='num'>{t.ast}</td>
                            <td className='num'>{t.stl}</td>
                            <td className='num'>{t.blk}</td>
                            <td className='num'>{t.tov}</td>
                            <td className='num'>{t.pf}</td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default BoxScore
